import { useState } from "react";
import { Link } from "react-router";
import {
  AlertTriangle, CheckCircle2, UserRound, ChevronDown, ExternalLink as ExternalIcon, Globe, Link2, Radio, RefreshCw, Search, ShieldAlert, WifiOff,
} from "lucide-react";
import type { IntelDocument, IntelView, LiveCoverage, LiveIssue, ManualLink, WebResult } from "../../../shared/contracts.js";
import { docsByUrl, groupRepublications, verificationOf } from "../../lib/evidence.js";
import { ExternalLink } from "../../components/ui/Misc.js";
import { Badge } from "../../components/ui/Badge.js";
import { Button } from "../../components/ui/Button.js";
import { Card } from "../../components/ui/Card.js";
import { Skeleton, Spinner } from "../../components/ui/States.js";
import { fmtDate, fmtMs, plural } from "../../lib/format.js";
import { categoryLabel, LIVE_ENTITY_LABELS, LIVE_STATUS_LABELS, providerLabel } from "../../lib/labels.js";
import { useMutation } from "@tanstack/react-query";
import { api } from "../../api.js";
import { useIsAdmin } from "../../components/AppShell.js";
import { useToast } from "../../components/ui/Toast.js";
import {
  adPhrase, borrowedMentions, groupLiveResults, isSourcePost, mentionsArea, publisherOf, queryKind, queryResults,
  type BorrowedMention, type LiveView, type PivotRequest,
} from "../../lib/live.js";

function StatusBadge({ live }: { live: LiveView }) {
  const s = live.status;
  if (s === "queued" || s === "running") {
    return (
      <Badge tone="accent" icon={<Spinner className="size-3.5" />}>
        {LIVE_STATUS_LABELS[s]}
      </Badge>
    );
  }
  if (s === "done") return <Badge tone="success" icon={<CheckCircle2 />}>{LIVE_STATUS_LABELS[s]}</Badge>;
  if (s === "partial") return <Badge tone="warning" icon={<AlertTriangle />}>{LIVE_STATUS_LABELS[s]}</Badge>;
  if (s === "error") return <Badge tone="danger" icon={<AlertTriangle />}>{LIVE_STATUS_LABELS[s]}</Badge>;
  return <Badge tone="neutral" icon={<WifiOff />}>{LIVE_STATUS_LABELS[s]}</Badge>;
}

/** "Publicado por @cuenta" / "En el grupo…": para abrir el perfil o el grupo y seguir buscando. */
function PublisherLine({ r }: { r: WebResult }) {
  const p = publisherOf(r);
  if (!p) return null;
  const prefix = p.kind === "grupo" ? "En" : p.kind === "pagina" ? "Página" : "Publicado por";
  return (
    <p className="flex items-center gap-1 text-[12.5px] text-muted">
      <UserRound className="size-3.5 shrink-0" aria-hidden="true" />
      <span>{prefix}</span>
      {p.url ? <ExternalLink href={p.url} className="font-medium">{p.label}</ExternalLink> : <span className="font-medium text-fg">{p.label}</span>}
    </p>
  );
}

/**
 * "Seguir la pista" desde un resultado: el mismo aviso en otros sitios, más de la
 * cuenta o grupo que lo publicó (con lo del caso) y, para administradores, dejar
 * esa cuenta o grupo como fuente del recolector (fotos guardadas + OCR).
 */
function TrailActions({ r, onPivot, busy, caseTerms }: { r: WebResult; onPivot: (p: PivotRequest) => void; busy: boolean; caseTerms: string }) {
  const isAdmin = useIsAdmin();
  const toast = useToast();
  const phrase = adPhrase(r.title);
  const pub = publisherOf(r);
  const site = pub?.url ?? null;
  const fuente = site && pub && (pub.kind === "grupo" || /instagram\.com\//.test(site))
    ? { categoria: pub.kind === "grupo" ? "grupos_comercial" as const : "ig" as const, valor: site }
    : null;
  const addSource = useMutation({
    mutationFn: () => api.admin.createTarget({ ...fuente!, notas: `Agregada desde la búsqueda en vivo (${r.domain})` }),
    onSuccess: () => toast.success("Fuente agregada: el recolector la visitará en su próximo ciclo."),
    onError: (e: Error) => toast.error(/existe|duplic/i.test(e.message) ? "Esa fuente ya estaba agregada." : e.message),
  });
  if (!phrase && !site) return null;
  const btn = "inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-[12px] font-medium text-muted hover:border-accent hover:text-fg disabled:opacity-50";
  return (
    <div className="flex flex-wrap gap-1.5 pt-0.5" aria-label="Seguir la pista">
      {phrase && (
        <button type="button" className={btn} disabled={busy} title={`Busca «${phrase}» tal cual en Instagram, Facebook, sitios de avisos, TikTok y YouTube`}
          onClick={() => onPivot({ label: `"${phrase}" en otros sitios`, pivot: { kind: "phrase", phrase } })}>
          <Search className="size-3" aria-hidden="true" />Este aviso en otros sitios
        </button>
      )}
      {site && pub && (
        <button type="button" className={btn} disabled={busy}
          title={caseTerms ? `Busca «${caseTerms}» y lo último publicado dentro de ${pub.label}` : `Lo publicado dentro de ${pub.label}`}
          onClick={() => onPivot({
            label: `${caseTerms || "publicaciones"} en ${site.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}`,
            pivot: { kind: "within", site, ...(caseTerms ? { terms: caseTerms } : {}) },
          })}>
          <UserRound className="size-3" aria-hidden="true" />Buscar en {pub.kind === "grupo" ? "este grupo" : pub.label}
        </button>
      )}
      {isAdmin && fuente && (
        <button type="button" className={btn} disabled={addSource.isPending || addSource.isSuccess}
          title="El recolector social la recorre: guarda sus publicaciones con fotos y el OCR lee las patentes"
          onClick={() => addSource.mutate()}>
          <Radio className="size-3" aria-hidden="true" />{addSource.isSuccess ? "Fuente agregada" : "Agregar como fuente"}
        </button>
      )}
    </div>
  );
}

/** Estado de verificación del resultado (página / OCR / solo vista previa) y su evidencia. */
function VerificationLine({ r, docs }: { r: WebResult; docs: Map<string, IntelDocument> }) {
  const [open, setOpen] = useState(false);
  const v = verificationOf(r, docs);
  return (
    <div className="text-[12px]">
      <span className="inline-flex flex-wrap items-center gap-1">
        <Badge tone={v.tone} title={v.hint}>{v.label}</Badge>
        {v.evidence.length > 0 && (
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="text-subtle hover:text-fg">
            {open ? "ocultar evidencia" : "ver evidencia"}
          </button>
        )}
      </span>
      {open && (
        <ul className="mt-1 space-y-1 border-l-2 border-border pl-2 text-[12px] text-muted">
          {v.evidence.map((e, i) => (
            <li key={i}>
              <span className="font-medium text-fg">{e.where === "pagina" ? "Página" : e.where === "ocr" ? "Foto (OCR)" : "Extracto del buscador"}</span>
              {e.kind === "person" && e.detail === "parcial" ? " · nombre parcial" : ""}
              {e.detail === "ejemplo" ? ` · en una demostración del sitio${e.note ? ` (${e.note})` : ""}` : ""}
              {": "}
              {e.where === "ocr"
                ? <a href={e.fragment} target="_blank" rel="noopener noreferrer nofollow" className="text-accent hover:underline">ver imagen</a>
                : <q>{e.fragment}</q>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WebResultItem({ r, inArea, borrowed, trail, docs, copies = [] }: {
  r: WebResult; inArea: boolean; borrowed?: BorrowedMention | undefined;
  trail?: { onPivot: (p: PivotRequest) => void; busy: boolean; caseTerms: string } | undefined;
  docs: Map<string, IntelDocument>;
  /** Republicaciones de la misma nota o aviso en otros sitios (cada una con su enlace y su evidencia). */
  copies?: WebResult[];
}) {
  return (
    <li className="space-y-1 border-b border-border py-2.5 last:border-b-0">
      <ExternalLink href={r.url} className="items-start font-medium leading-snug" showIcon={false}>
        <span className="line-clamp-2">{r.title || r.url}</span>
        <ExternalIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
      </ExternalLink>
      <div className="flex flex-wrap items-center gap-1 text-[12px] text-subtle">
        <span className="max-w-[14rem] truncate font-medium text-muted">{r.domain}</span>
        <Badge tone="neutral">{categoryLabel(r.category)}</Badge>
        {isSourcePost(r) && (
          <Badge tone="success" title="Google no lo devolvía al buscar el dato: se encontró con el texto que otras publicaciones copiaban de este post (la descripción automática de su foto, que Instagram genera leyendo la imagen)">
            Publicación de origen
          </Badge>
        )}
        {borrowed ? (
          <Badge tone="warning" title="El dato aparece en el extracto porque Instagram/Facebook muestra junto a este post otra publicación que sí lo tiene (del mismo perfil o sugerida). Este post no lo menciona.">
            Mención de otra publicación
          </Badge>
        ) : isSourcePost(r) ? null : r.exactMatch ? (
          <Badge tone="success" title="El nombre o la patente aparece completo en el título o el extracto">
            Coincidencia exacta
          </Badge>
        ) : (
          <Badge tone="warning" title="El resultado no repite el dato completo: puede tratarse de otra persona o vehículo">
            Posible homónimo
          </Badge>
        )}
        {inArea && <Badge tone="accent" title="Menciona la comuna o región indicada en la búsqueda">En la zona</Badge>}
        {r.publishedAt && <span>{fmtDate(r.publishedAt)}</span>}
      </div>
      {r.snippet && <p className="line-clamp-3 text-[12.5px] leading-snug text-muted">{r.snippet}</p>}
      <VerificationLine r={r} docs={docs} />
      {copies.length > 0 && (
        <details className="group text-[12.5px]">
          <summary className="flex cursor-pointer list-none items-center gap-1 text-muted hover:text-fg">
            También publicado en {plural(copies.length, "sitio más", "sitios más")} (cuenta como una sola publicación)
            <ChevronDown className="size-3.5 transition-transform group-open:rotate-180" aria-hidden="true" />
          </summary>
          <ul className="mt-1 space-y-1.5 border-l-2 border-border pl-2">
            {copies.map((c) => (
              <li key={c.id}>
                <ExternalLink href={c.url} className="font-medium">{c.domain}</ExternalLink>
                {c.publishedAt && <span className="ml-1 text-subtle">{fmtDate(c.publishedAt)}</span>}
                <VerificationLine r={c} docs={docs} />
              </li>
            ))}
          </ul>
        </details>
      )}
      <PublisherLine r={r} />
      {trail && <TrailActions r={r} {...trail} />}
      {borrowed?.sourceUrl && (
        <ExternalLink href={borrowed.sourceUrl} className="text-[12.5px]">Ver la publicación de origen</ExternalLink>
      )}
      {r.listingId && (
        <Link
          to={`/avisos/${encodeURIComponent(r.listingId)}`}
          className="inline-flex items-center gap-1 text-[12.5px] font-medium text-accent hover:underline"
        >
          <Link2 className="size-3.5" aria-hidden="true" />
          Ya está en SENTINEL: ver aviso
        </Link>
      )}
    </li>
  );
}

/** Redes que se buscan (vía Google) y cómo reconocer sus resultados por dominio. */
const SOCIAL_NETWORKS: { label: string; re: RegExp }[] = [
  { label: "Facebook", re: /(^|\.)facebook\.com$|(^|\.)fb\.com$/ },
  { label: "Instagram", re: /(^|\.)instagram\.com$/ },
  { label: "TikTok", re: /(^|\.)tiktok\.com$/ },
  { label: "X (Twitter)", re: /(^|\.)(x|twitter)\.com$/ },
  { label: "LinkedIn", re: /(^|\.)linkedin\.com$/ },
  { label: "YouTube", re: /(^|\.)youtube\.com$|(^|\.)youtu\.be$/ },
  { label: "Reddit", re: /(^|\.)reddit\.com$/ },
  { label: "Threads", re: /(^|\.)threads\.(net|com)$/ },
];

/** Resumen por red social: con resultados primero (exactos / posibles); las vacías en una línea. */
function SocialSummary({ results, searched, active, borrowed }: { results: WebResult[]; searched: boolean; active: boolean; borrowed: Map<string, BorrowedMention> }) {
  if (!searched) return null;
  const rows = SOCIAL_NETWORKS.map((n) => {
    const rs = results.filter((r) => n.re.test(r.domain.replace(/^www\./, "")));
    const exact = rs.filter((r) => r.exactMatch && !borrowed.has(r.id)).length;
    return { label: n.label, exact, loose: rs.length - exact };
  });
  const found = rows.filter((r) => r.exact + r.loose > 0).sort((x, y) => y.exact - x.exact || y.loose - x.loose);
  const empty = rows.filter((r) => r.exact + r.loose === 0);
  return (
    <section aria-label="Redes sociales" className="border-b border-border py-3">
      <h3 className="mb-2 text-[12px] font-semibold tracking-wide text-subtle uppercase">Redes sociales</h3>
      {found.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {found.map((r) => (
            <li key={r.label} className="flex items-center gap-2 px-2.5 py-1.5 text-[13px]">
              <span className="min-w-0 flex-1 truncate font-medium text-fg">{r.label}</span>
              {r.exact > 0 && (
                <Badge tone="success" title="Resultados que repiten el nombre o la patente completo">
                  <span className="tabular-nums">{r.exact}</span> exacto{r.exact === 1 ? "" : "s"}
                </Badge>
              )}
              {r.loose > 0 && (
                <Badge tone="neutral" title="No repiten el dato completo: pueden ser homónimos">
                  <span className="tabular-nums">{r.loose}</span> posible{r.loose === 1 ? "" : "s"}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
      {empty.length > 0 && (
        <p className="mt-2 text-[12.5px] text-subtle">
          {active ? "Buscando en: " : "Sin coincidencias en la consulta a: "}
          <span className="text-muted">{empty.map((r) => r.label).join(" · ")}</span>
        </p>
      )}
      <p className="mt-1.5 text-[11.5px] leading-snug text-subtle">
        Consulta realizada sobre perfiles, páginas y posts públicos que Google tiene indexados; no es una revisión completa de cada red. Los grupos de Facebook se cubren con la recolección propia.
      </p>
    </section>
  );
}

const STAGE_LABEL: Record<number, string> = { 1: "Identificación", 2: "Contexto", 3: "Fuentes específicas", 4: "Profundización" };
const ISSUE_LABEL: Record<LiveIssue, string> = {
  tiempo_agotado: "tiempo agotado",
  sin_creditos: "buscador sin créditos",
  limite_proveedor: "límite de consultas del buscador",
  error_proveedor: "error del buscador",
  guardado: "error al guardar resultados",
};

/**
 * Cobertura real de la corrida: consultas ejecutadas por etapa, lo que quedó
 * pendiente y por qué, cuánto vino de la caché (con botón para actualizar).
 */
function CoverageBlock({ live, onRefresh, refreshing }: { live: LiveView; onRefresh?: (() => void) | undefined; refreshing: boolean }) {
  const cov: LiveCoverage | null | undefined = live.coverage;
  const done = live.status === "done" || live.status === "partial";
  if (!done || live.queries.length === 0) return null;
  const pendingIds = new Set((cov?.pending ?? []).map((p) => p.id));
  const stages = [1, 2, 3, 4].map((st) => {
    const qs = live.queries.filter((q) => (q.stage ?? 2) === st);
    return { st, total: qs.length, ran: qs.filter((q) => !pendingIds.has(q.id)).length };
  }).filter((x) => x.total > 0);
  const cached = cov?.cachedCalls ?? live.providers.filter((p) => p.cached).length;
  const when = live.refreshedAt ?? live.finishedAt;
  return (
    <section aria-label="Cobertura de la búsqueda" className="space-y-1 border-b border-border py-2.5 text-[12.5px] text-muted">
      <p>
        <span className="font-medium text-fg">Consultas ejecutadas: </span>
        {stages.map((x) => `${STAGE_LABEL[x.st]} ${x.ran} de ${x.total}`).join(" · ")}
      </p>
      {cov && cov.pending.length > 0 && (
        <p className="text-warning-soft-fg">
          {plural(cov.pending.length, "consulta no alcanzó", "consultas no alcanzaron")} a ejecutarse
          ({[...new Set(cov.pending.map((p) => STAGE_LABEL[p.stage]))].join(", ")}). Cobertura pendiente.
        </p>
      )}
      {cov && cov.issues.length > 0 && (
        <p className="text-warning-soft-fg">Problemas: {cov.issues.map((i) => ISSUE_LABEL[i]).join(" · ")}.</p>
      )}
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>
          {cached > 0
            ? `${plural(cached, "respuesta reutilizada", "respuestas reutilizadas")} de búsquedas recientes (hasta 24 h)`
            : "Todas las respuestas consultadas en el momento"}
          {when ? ` · ${live.refreshedAt ? "actualizado" : "consultado"} ${fmtDate(when)} ${new Date(when).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}` : ""}
        </span>
        {onRefresh && (
          <button type="button" onClick={onRefresh} disabled={refreshing}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-[12px] font-medium text-muted hover:border-accent hover:text-fg disabled:opacity-50">
            <RefreshCw className={`size-3 ${refreshing ? "animate-spin" : ""}`} aria-hidden="true" />Actualizar
          </button>
        )}
      </p>
      <p className="text-[11.5px] text-subtle">
        Una consulta a un sitio (por ejemplo, en Facebook) solo cubre lo que el buscador tiene indexado: no es una revisión completa del sitio.
      </p>
    </section>
  );
}

export function ManualLinks({ links }: { links: ManualLink[] }) {
  if (links.length === 0) return null;
  return (
    <div className="border-b border-border bg-surface-2/60 px-4 py-3">
      <h3 className="mb-1 text-[13px] font-semibold text-fg">Consultas manuales sugeridas</h3>
      <p className="mb-2 text-[12px] text-subtle">
        Fuentes oficiales con captcha o ClaveÚnica: ábrelas y consulta a mano.
      </p>
      <ul className="space-y-1.5">
        {links.map((l) => (
          <li key={l.url} className="text-[13px]">
            <ExternalLink href={l.url}>{l.label}</ExternalLink>
            {l.note && <p className="text-[12px] text-subtle">{l.note}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function LivePanel({
  live: liveAll, requested, disabledReason, manualLinks, area = [], keywords = [], onPivot, pivotBusy = false, caseTerms = "", showManualLinks = true,
  intel, onRefresh, refreshing = false,
}: {
  live: LiveView;
  /** Si el analista pidió la búsqueda en vivo para esta consulta. */
  requested: boolean;
  disabledReason: string | null;
  manualLinks: ManualLink[];
  /** Comuna/región de la búsqueda: sus resultados van primero y se pueden filtrar. */
  area?: string[];
  /** Palabras clave del caso (plegadas): los resultados que traen alguna suben. */
  keywords?: string[];
  /** "Seguir la pista": abre una búsqueda nueva desde un resultado. */
  onPivot?: ((p: PivotRequest) => void) | undefined;
  pivotBusy?: boolean;
  /** Lo del caso para buscar dentro de una cuenta o grupo (marca y modelo, o la patente). */
  caseTerms?: string;
  /** false si las consultas manuales se muestran en otra pestaña ("Búsquedas recomendadas"). */
  showManualLinks?: boolean;
  /** Análisis profundo (páginas leídas): da el estado de verificación de cada resultado. */
  intel?: IntelView | undefined;
  /** «Actualizar»: vuelve a consultar sin usar la caché. */
  onRefresh?: (() => void) | undefined;
  refreshing?: boolean;
}) {
  const docs = docsByUrl(intel);
  // Los resultados de las pistas (nivel 2) van en "Búsquedas recomendadas", no aquí.
  const live = liveAll.results.some((r) => r.level === 2) ? { ...liveAll, results: liveAll.results.filter((r) => r.level !== 2) } : liveAll;
  const trail = onPivot ? { onPivot, busy: pivotBusy, caseTerms } : undefined;
  const active = live.status === "queued" || live.status === "running";
  const [onlyArea, setOnlyArea] = useState(false);
  const visible = onlyArea && area.length > 0 ? live.results.filter((r) => mentionsArea(r, area)) : live.results;
  const groups = groupLiveResults(visible, area, keywords);
  const inAreaCount = area.length > 0 ? live.results.filter((r) => mentionsArea(r, area)).length : 0;
  const borrowed = borrowedMentions(live.results);
  const failedProviders = live.providers.filter((p) => !p.ok);

  return (
    <Card aria-labelledby="live-title" role="region" className="overflow-hidden">
      <div className="flex items-start gap-2 border-b border-border px-4 py-3">
        <Globe className="mt-0.5 size-4 text-muted" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h2 id="live-title" className="text-[14px] font-semibold text-fg">
            Internet en vivo <span className="font-normal text-subtle">(no verificado)</span>
          </h2>
          <p className="text-[12px] text-subtle">
            Resultados de buscadores públicos al momento de la consulta. Pueden referirse a homónimos.
          </p>
        </div>
        {requested && <StatusBadge live={live} />}
      </div>
      {showManualLinks && <ManualLinks links={manualLinks} />}

      <div aria-live="polite" aria-busy={active} className="px-4">
        {!requested || live.status === "disabled" ? (
          <div className="flex items-start gap-2 py-4 text-[13px] text-muted">
            <Radio className="mt-0.5 size-4 shrink-0 text-subtle" aria-hidden="true" />
            <p>
              {disabledReason ??
                live.reason ??
                "No se buscó en internet para esta consulta. Activa «Buscar también en internet» y vuelve a buscar."}
            </p>
          </div>
        ) : (
          <>
            {live.queries.length > 0 && (
              <details className="group border-b border-border py-2.5 text-[12.5px]">
                <summary className="flex cursor-pointer list-none items-center gap-1 font-medium text-muted hover:text-fg">
                  <Search className="size-3.5" aria-hidden="true" />
                  {plural(live.queries.length, "consulta enviada", "consultas enviadas")}
                  <ChevronDown className="ml-auto size-3.5 transition-transform group-open:rotate-180" aria-hidden="true" />
                </summary>
                <ul className="mt-2 space-y-1">
                  {live.queries.map((q) => {
                    const kind = queryKind(q);
                    const n = queryResults(live.providers, q.id);
                    return (
                      <li key={q.id} className="flex items-start gap-2">
                        <Badge tone="outline">{LIVE_ENTITY_LABELS[q.entity as WebResult["entity"]] ?? q.entity}</Badge>
                        {kind && <Badge tone="outline" className="border-dashed">{kind}</Badge>}
                        <code className="min-w-0 flex-1 font-mono text-[12px] break-words text-fg">{q.text}</code>
                        {n !== null && <span className="shrink-0 text-[11.5px] tabular-nums text-subtle">{plural(n, "resultado", "resultados")}</span>}
                      </li>
                    );
                  })}
                </ul>
                {live.providers.length > 0 && (
                  <p className="mt-2 text-[12px] text-subtle">
                    {[...new Set(live.providers.map((p) => providerLabel(p.provider)))].join(", ")} ·{" "}
                    {fmtMs(Math.max(...live.providers.map((p) => p.ms)))}
                    {live.providers.some((p) => p.cached) && " · algunas respuestas desde caché"}
                    {(() => {
                      const n = live.providers.reduce((acc, p) => acc + (p.filtered ?? 0), 0);
                      return n > 0 ? ` · ${n} resultado${n === 1 ? "" : "s"} descartado${n === 1 ? "" : "s"} (tiendas extranjeras o sin mención de lo buscado)` : "";
                    })()}
                  </p>
                )}
              </details>
            )}

            <CoverageBlock live={live} onRefresh={onRefresh} refreshing={refreshing} />

            {active && live.results.length === 0 && (
              <div className="space-y-3 py-3" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="space-y-1.5">
                    <Skeleton className="h-4 w-11/12" />
                    <Skeleton className="h-3 w-1/3" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                ))}
              </div>
            )}
            {active && <p className="sr-only">Buscando en internet…</p>}

            {area.length > 0 && live.results.length > 0 && (
              <label className="flex items-center gap-2 border-b border-border py-2.5 text-[13px] text-muted">
                <input type="checkbox" checked={onlyArea} onChange={(e) => setOnlyArea(e.target.checked)} className="size-4 accent-[var(--color-accent)]" />
                <span>
                  Solo resultados de <span className="font-medium text-fg">{area.join(" / ")}</span>
                  <span className="text-subtle"> ({inAreaCount} de {live.results.length}; los de la zona ya aparecen primero)</span>
                </span>
              </label>
            )}

            <SocialSummary results={visible} searched={live.queries.some((q) => /^S[PVCTME]/.test(q.id))} active={active} borrowed={borrowed} />

            {groups.map((g) => {
              // Las menciones prestadas de otra publicación no cuentan como coincidencia.
              const exact = g.items.filter((r) => r.exactMatch && !borrowed.has(r.id));
              const loose = g.items.filter((r) => !r.exactMatch || borrowed.has(r.id));
              // Republicaciones juntas: cuentan como una sola publicación (cada enlace se conserva).
              const exactGroups = groupRepublications(exact);
              const looseGroups = groupRepublications(loose);
              return (
                <section key={g.entity} aria-label={LIVE_ENTITY_LABELS[g.entity]} className="py-2">
                  <h3 className="pt-1 text-[12px] font-semibold tracking-wide text-subtle uppercase">
                    {LIVE_ENTITY_LABELS[g.entity]} · {plural(exactGroups.length, "coincidencia", "coincidencias")}
                  </h3>
                  {exact.length > 0 ? (
                    <ul>{exactGroups.map(({ main: r, copies }) => <WebResultItem key={r.id} r={r} copies={copies} docs={docs} inArea={mentionsArea(r, area)} borrowed={borrowed.get(r.id)} trail={trail} />)}</ul>
                  ) : (
                    <p className="py-1.5 text-[12.5px] text-muted">Ningún resultado repite el dato completo.</p>
                  )}
                  {loose.length > 0 && (
                    // Los que no repiten el nombre/patente completo: plegados (suelen ser homónimos o ruido).
                    <details className="group">
                      <summary className="flex cursor-pointer list-none items-center gap-1 py-1.5 text-[12.5px] font-medium text-muted hover:text-fg">
                        {plural(loose.length, "posible homónimo", "posibles homónimos")}
                        <ChevronDown className="size-3.5 transition-transform group-open:rotate-180" aria-hidden="true" />
                      </summary>
                      <ul>{looseGroups.map(({ main: r, copies }) => <WebResultItem key={r.id} r={r} copies={copies} docs={docs} inArea={mentionsArea(r, area)} borrowed={borrowed.get(r.id)} trail={trail} />)}</ul>
                    </details>
                  )}
                </section>
              );
            })}

            {!active && live.results.length === 0 && live.status === "done" && (
              <p className="py-4 text-[13px] text-muted">
                No se encontraron coincidencias en las consultas y fuentes ejecutadas. Esto no significa que no existan antecedentes.
              </p>
            )}
            {(live.status === "error" || live.status === "partial" || (!live.coverage && failedProviders.length > 0)) && (
              <div className="flex items-start gap-2 py-3 text-[12.5px] text-danger-soft-fg">
                <ShieldAlert className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden="true" />
                <p>
                  {live.error ??
                    (failedProviders.length > 0
                      ? `Fallaron ${plural(failedProviders.length, "consulta", "consultas")} al proveedor; los resultados pueden estar incompletos.`
                      : "La búsqueda en internet terminó con errores.")}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
